import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll behavior manager:
 * - New navigations (PUSH / REPLACE) start at the top of the page.
 * - Back / forward navigations (POP) restore the previous scroll position.
 * - Hash links (#section) scroll to the target element.
 */
const positions = new Map();

export default function ScrollManager() {
    const location = useLocation();
    const navType = useNavigationType();
    const currentKey = useRef(location.key);

    // Let us control restoration instead of the browser.
    useEffect(() => {
        if ("scrollRestoration" in window.history) {
            const previous = window.history.scrollRestoration;
            window.history.scrollRestoration = "manual";
            return () => {
                window.history.scrollRestoration = previous;
            };
        }
        return undefined;
    }, []);

    // Continuously record the scroll position for the active history entry.
    useEffect(() => {
        currentKey.current = location.key;
        let frame = null;
        const onScroll = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = null;
                positions.set(currentKey.current, window.scrollY);
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            positions.set(currentKey.current, window.scrollY);
            window.removeEventListener("scroll", onScroll);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [location.key]);

    useLayoutEffect(() => {
        const prefersReduced =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (navType === "POP") {
            const saved = positions.get(location.key);
            if (typeof saved === "number") {
                // Wait for the route content to paint before restoring.
                const restore = () => window.scrollTo(0, saved);
                restore();
                const t1 = window.setTimeout(restore, 60);
                const t2 = window.setTimeout(restore, 240);
                return () => {
                    window.clearTimeout(t1);
                    window.clearTimeout(t2);
                };
            }
        }

        if (location.hash) {
            const id = decodeURIComponent(location.hash.slice(1));
            const target = document.getElementById(id);
            if (target) {
                target.scrollIntoView({
                    behavior: prefersReduced ? "auto" : "smooth",
                    block: "start",
                });
                return undefined;
            }
        }

        window.scrollTo(0, 0);
        return undefined;
    }, [location.key, location.pathname, location.hash, navType]);

    return null;
}
